import type { TranslationKeys } from '../types';

const en: TranslationKeys = {
  nav: {
    home: 'Home',
    blockExplorer: 'Explorer',
    swap: 'Swap',
    exchange: 'Exchange',
    docs: 'Docs',
    more: 'More',
    close: 'Close',
    bridge: 'Bridge',
  },
  bridge: {
    title: 'Igra Bridge',
    subtitle: 'Bridge KAS between Kaspa and Igra (chain {chainId})',
    tabDeposit: 'Deposit KAS',
    tabDepositSub: 'KAS → iKAS',
    tabWithdraw: 'Withdraw KAS',
    tabWithdrawSub: 'iKAS → KAS',
    chainKaspa: 'Kaspa',
    chainEvm: 'EVM (Igra)',
    useWallet: 'Use wallet',
    gate: {
      installTitle: 'Connect KasWare Wallet',
      installDesc:
        'The bridge is signed entirely inside KasWare. Install the extension or open this page in the KasWare Telegram app.',
      disconnectedTitle: 'Wallet disconnected',
      connectWallet: 'Connect wallet',
      connectTitle: 'Connect {chain}',
      connectDesc: 'Authorise the {chain} account in KasWare to continue.',
      connecting: 'Connecting...',
      connectChain: 'Connect {chain}',
    },
    deposit: {
      youSend: 'You send (Kaspa L1)',
      youReceive: 'You receive (Igra L2)',
      recipientLabel: 'Receive iKAS at this Igra address',
      fromWallet: '(from wallet)',
      badAddress: 'Enter a valid EVM address — a mixed-case address must pass the EIP-55 checksum.',
      connectEvm: 'Connect EVM to auto-fill',
      connectingEvm: 'Connecting EVM...',
      infoMin: 'Minimum deposit: {min} KAS.',
      infoMining:
        'The transaction id is mined in your browser to start with 97b1 — this usually takes a few seconds.',
      infoMint: 'iKAS is minted after the Igra sequencer confirms the Entry on Kaspa.',
      phaseConnecting: 'Connecting to Kaspa network...',
      phaseFetching: 'Fetching your UTXOs...',
      phaseMining: 'Mining a valid transaction id... ({tries} tries)',
      phaseSigning: 'Waiting for your signature in KasWare...',
      phaseBroadcasting: 'Broadcasting the Entry transaction...',
      cancelMining: 'Cancel mining',
      successTitle: 'Deposit submitted',
      successMined: 'Valid tx id found after {tries} tries (nonce {nonce}).',
      kaspaTx: 'Kaspa transaction:',
      prefixWarning:
        'The broadcast transaction id no longer starts with 97b1. Igra may not credit this deposit — keep the transaction id and contact support.',
      newDeposit: 'New deposit',
      cta: 'Bridge KAS to Igra',
      ctaAmount: 'Bridge {amount} KAS to Igra',
    },
    withdraw: {
      youBurn: 'You burn (Igra L2)',
      paidOut: 'Paid out on (Kaspa L1)',
      payoutLabel: 'KAS payout address',
      infoLimits: 'Limits: {min} – {max} iKAS per withdrawal.',
      infoFeeZero: 'Protocol fee: currently 0.',
      infoFeeQuoted: 'Protocol fee: quoted on request.',
      infoRate: '1 iKAS burns to exactly 1 KAS of payout (no spread).',
      warningPrefix: 'Withdrawals are',
      warningStrong: 'not instant',
      warningRest:
        'the iKAS is burned on Igra, then a multi-signature committee releases the KAS on Kaspa after reviewing the request. Only send amounts you are comfortable waiting for.',
      checking: 'Checking...',
      modalTitle: 'Confirm withdrawal',
      modalBurn: 'You burn',
      modalFee: 'Protocol fee',
      modalPayout: 'KAS paid to',
      modalAck:
        'I understand that release is manual, reviewed by a multi-signature committee, and is not instant.',
      modalConfirm: 'Confirm and burn iKAS',
      modalBack: 'Back',
      successTitle: 'Exit request confirmed on Igra',
      requestId: 'Exit request id: {id}',
      igraTx: 'Igra transaction:',
      successPayout: 'KAS will be sent to:',
      successNote: 'KAS will be released to your payout address after the multi-signature review.',
      newWithdrawal: 'New withdrawal',
      requesting: 'Requesting exit...',
      cta: 'Burn iKAS and request KAS',
      ctaAmount: 'Burn {amount} iKAS and request KAS',
    },
  },
  home: {
    tagline: 'KasWare Wallet',
    hero: {
      subtitle: 'One self-custodial wallet for TON, Kaspa and EVM chains — your keys stay on your device.',
      chainsLabel: 'Supported networks',
      ctaDownload: 'Add to Chrome',
      ctaTelegram: 'Open in Telegram',
      note: 'Free, no sign-up, no email required.',
    },
    pillars: {
      security: {
        title: 'Secure by design',
        desc: 'Keys are generated and encrypted on your device. Every transaction is screened and shown in full before you sign.',
      },
      convenience: {
        title: 'Everything in one place',
        desc: 'Hold, send, swap and bridge across TON, Kaspa and EVM without juggling several wallets or accounts.',
      },
      friendly: {
        title: 'Made for people',
        desc: 'Plain-language prompts, readable addresses, dark and light themes, and seven languages out of the box.',
      },
      reliable: {
        title: 'Dependable',
        desc: 'Open transaction history, multiple network endpoints and a wallet that stays usable even when one provider is down.',
      },
    },
    content: {
      downloadTitle: 'Download',
      securityTitle: 'How your funds stay yours',
      p1: 'KasWare is a self-custodial wallet for Ton, Kaspa and EVM.',
      p2: 'We never have access to your funds. Your private keys are encrypted on your device by your password and never shared with anyone.',
      p3: 'All accounts are deterministically derived from your Secret Recovery Phrase, giving you full ownership and control.',
    },
  },
  about: {
    contact: 'Contact Us',
    contacts: {
      twitter: 'Twitter',
      telegram: 'Telegram',
      discord: 'Discord',
      email: 'Email',
    },
  },
  footer: {
    privacy: 'Privacy Policy',
    copyright: 'Copyright © {year} Flying Rabbit Tech Ltd',
  },
  explorer: {
    title: 'Transaction Explorer',
    subtitle: 'Search for Kaspa and KRC-20 transaction records by address',
    timeRange: 'Time Range:',
    to: 'to',
    clear: 'Clear',
    address: 'Address:',
    kasTab: 'KAS Transfers',
    krc20Tab: 'KRC-20 Operations',
    enterAddress: 'Enter a Kaspa address to view transactions',
    loading: 'Loading...',
    search: {
      placeholder: 'Enter Kaspa address (kaspa:q...)',
      button: 'Search',
    },
    tx: {
      loading: 'Loading transactions...',
      empty: 'No transactions found for this address',
      emptyInRange: 'No transactions found for this address in the selected time range',
      failed: 'Failed to load transactions: {error}',
      retry: 'Retry',
    },
    krc20: {
      loading: 'Loading KRC-20 operations...',
      empty: 'No KRC-20 operations found for this address',
      emptyInRange: 'No KRC-20 operations found for this address in the selected time range',
      failed: 'Failed to load KRC-20 operations: {error}',
      retry: 'Retry',
    },
  },
  languageNames: {
    en: 'EN',
    id: 'ID',
    fr: 'FR',
    zh_CN: '简体中文',
    zh_TW: '繁體中文',
    es: 'ES',
    th: 'TH',
  },
};

export default en;
